import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/loader/Loader";
import axios from "axios";
import toast from "react-hot-toast";
import {
  storeAllRegionList,
  storeCategoryList,
  storeFlavourList,
  storeRegionList,
  storeSubCategoryList,
  storeTaxList,
  storeUnitList,
  storeWeightList,
  storeShopList,
  storeShapeList,
  storeCategoryCodeList,
  storePromoList,
  storeCustomPromoList,
  storeFlatpriceList,
  storeCustomerList,
} from "../../redux/counterSlice";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PeopleIcon from "@mui/icons-material/People";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationOnIcon from "@mui/icons-material/LocationOn";

function DashboardIndex() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.counter?.userData);

  // Get login type and IDs from sessionStorage
  const loginType = sessionStorage.getItem("loginType");
  const shopId = sessionStorage.getItem("shop_id");
  const shopName = sessionStorage.getItem("shop_name");
  const regionId = sessionStorage.getItem("region_id");
  const locationDetailsId = sessionStorage.getItem("location_details_id");
  const dispatchName = sessionStorage.getItem("dispatch_name");

  // Check user types
  const isShopUser = loginType === "shop" && shopId;
  const isDispatchUser = loginType === "dispatch" && regionId;

  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [type, setType] = useState("monthly");

  const loadInitialData = async () => {
    try {
      setLoader(true);
      const [
        taxResponse,
        categoryList,
        subCategoryList,
        flavourList,
        weightList,
        unitList,
        regionList,
        allRegionList,
        allShopList,
        shapeList,
        categoryCodeList,
        promoResponse,
        customPromoList,
        flatPriceList,
        customerList,
      ] = await axios.all([
        axios.get("/getAllActiveTaxList"),
        axios.get("/category"),
        axios.get("/subcategory"),
        axios.get("/getAllActiveFlavourList"),
        axios.get("/getAllActiveWeightList"),
        axios.get("/getAllActiveUnitList"),
        axios.get("/getAllActiveRegionList"),
        axios.get("/getAllRegionList"),
        axios.get("/getAllShopDetails"),
        axios.get("/shape"),
        axios.get("/category_code"),
        axios.get("/promo"),
        axios.get("/custom_promos"),
        axios.get("/offerFixedPrice"),
        axios.get("/getAllCustomerDetails"),
      ]);
      taxResponse?.data?.data?.length > 0 &&
        dispatch(storeTaxList(taxResponse?.data?.data));
      categoryList?.data?.data?.length > 0 &&
        dispatch(storeCategoryList(categoryList?.data?.data));
      subCategoryList?.data?.data?.length > 0 &&
        dispatch(storeSubCategoryList(subCategoryList?.data?.data));
      flavourList?.data?.data?.length > 0 &&
        dispatch(storeFlavourList(flavourList?.data?.data));
      weightList?.data?.data?.length > 0 &&
        dispatch(storeWeightList(weightList?.data?.data));
      unitList?.data?.data?.length > 0 &&
        dispatch(storeUnitList(unitList?.data?.data));
      regionList?.data?.data?.length > 0 &&
        dispatch(storeRegionList(regionList?.data?.data));
      allRegionList?.data?.data?.length > 0 &&
        dispatch(storeAllRegionList(allRegionList?.data?.data));
      allShopList?.data?.data?.length > 0 &&
        dispatch(storeShopList(allShopList?.data?.data));
      shapeList?.data?.data?.length > 0 &&
        dispatch(storeShapeList(shapeList?.data?.data));
      categoryCodeList?.data?.data?.length > 0 &&
        dispatch(storeCategoryCodeList(categoryCodeList?.data?.data));
      promoResponse?.data?.data?.length > 0 &&
        dispatch(storePromoList(promoResponse?.data?.data));
      customPromoList?.data?.data?.length > 0 &&
        dispatch(storeCustomPromoList(customPromoList?.data?.data));
      flatPriceList?.data?.data?.length > 0 &&
        dispatch(storeFlatpriceList(flatPriceList?.data?.data));
      customerList?.data?.data?.length > 0 &&
        dispatch(storeCustomerList(customerList?.data?.data));
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
    }
  };

  const dashboardData = async () => {
    try {
      setLoader(true);

      // Choose API endpoint based on user type
      let apiUrl = `/dashboard-data/${type}`;
      let config = {};

      // ✅ CHECK FOR SHOP USER
      if (isShopUser && shopId) {
        apiUrl = `/shop-dashboard-data/${type}`;
        config = {
          params: {
            shop_id: shopId,
          },
        };
        console.log("Fetching shop dashboard data for shop ID:", shopId);
      }
      // ✅ CHECK FOR DISPATCH USER
      else if (isDispatchUser && regionId) {
        apiUrl = `/dispatch-dashboard-data/${type}`;
        config = {
          params: {
            region_id: regionId,
          },
        };
        console.log(
          "Fetching dispatch dashboard data for region ID:",
          regionId,
        );
      }

      // Always use GET request
      const res = await axios.get(apiUrl, config);

      console.log("Dashboard Data:", res?.data?.data);

      // Process the data to ensure all labels are strings
      const rawData = res?.data?.data;
      const processedData = {
        ...rawData,
        sales_trends: rawData?.sales_trends
          ? {
              labels: (rawData.sales_trends.labels || []).map((label) =>
                String(label || ""),
              ),
              data: rawData.sales_trends.data || [],
            }
          : { labels: [], data: [] },
        customer_growth: rawData?.customer_growth
          ? {
              ...rawData.customer_growth,
              labels: (rawData.customer_growth.labels || []).map((label) =>
                String(label || ""),
              ),
            }
          : { labels: [], data: [] },
        orders_by_region: (rawData?.orders_by_region || []).map((region) => ({
          ...region,
          region_name: String(region.region_name || ""),
        })),
        top_selling_products: (rawData?.top_selling_products || []).map(
          (product) => ({
            ...product,
            product_name: String(product.product_name || ""),
          }),
        ),
        top_selling_categories: (rawData?.top_selling_categories || []).map(
          (category) => ({
            ...category,
            category_name: String(category.category_name || ""),
          }),
        ),
      };

      setFormData(processedData);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch dashboard data");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    dashboardData();
  }, [type]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const formatLabel = (label) => {
    if (!label) return "";
    const [year, month] = label.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  // KPI Stat Card Component
  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <Card
      elevation={2}
      sx={{
        borderLeft: `4px solid ${color}`,
        height: "100%",
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              gutterBottom
            >
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}20`,
              color: color,
              width: 56,
              height: 56,
            }}
          >
            <Icon sx={{ fontSize: 28 }} />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  // Overview Tab Content
  const OverviewTab = () => (
    <Box>
      {/* KPI Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={MonetizationOnIcon}
            title="Current Revenue"
            value={`₹${(
              formData?.sales_trends?.data?.[
                formData?.sales_trends?.data?.length - 1
              ] || 0
            ).toLocaleString()}`}
            subtitle={formatLabel(
              formData?.sales_trends?.labels?.[
                formData?.sales_trends?.labels?.length - 1
              ],
            )}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={ShoppingCartIcon}
            title="Total Orders"
            value={
              formData?.order_status_summary?.reduce(
                (sum, item) => sum + item.count,
                0,
              ) || 0
            }
            subtitle={`${((formData?.conversion_rate || 0) * 100).toFixed(
              0,
            )}% conversion rate`}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PeopleIcon}
            title="New Customers"
            value={(
              formData?.customer_growth?.new_customers || 0
            ).toLocaleString()}
            subtitle={`${formData?.customer_growth?.data?.[formData?.customer_growth?.data?.length - 1] || 0} in ${formatLabel(formData?.customer_growth?.labels?.[formData?.customer_growth?.labels?.length - 1])}`}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={LocalShippingIcon}
            title="Orders Delivered"
            value={
              formData?.order_status_summary?.find(
                (s) => s.label === "Delivered",
              )?.count || 0
            }
            subtitle={`${(
              ((formData?.order_status_summary?.find(
                (s) => s.label === "Delivered",
              )?.count || 0) /
                (formData?.order_status_summary?.reduce(
                  (sum, item) => sum + item.count,
                  0,
                ) || 1)) *
              100
            ).toFixed(0)}% delivery rate`}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TrendingUpIcon sx={{ color: "#3b82f6", mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Sales Trend (Last 6 Months)
                </Typography>
              </Box>
              {formData?.sales_trends?.labels?.length > 0 ? (
                <LineChart
                  xAxis={[
                    {
                      data: formData.sales_trends.labels.map(
                        (label, idx) => idx,
                      ),
                      scaleType: "point",
                      valueFormatter: (value) =>
                        formData.sales_trends.labels[value],
                    },
                  ]}
                  series={[
                    {
                      data: formData.sales_trends.data,
                      label: "Sales",
                      color: "#3b82f6",
                      curve: "linear",
                    },
                  ]}
                  height={300}
                  margin={{ left: 70, right: 20, top: 20, bottom: 40 }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Order Status Distribution
              </Typography>
              {formData?.order_status_summary?.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: formData.order_status_summary.map((s, i) => ({
                        id: i,
                        value: s.count,
                        label: s.label,
                        color:
                          s.label === "Delivered"
                            ? "#10b981"
                            : s.label === "Submitted"
                              ? "#3b82f6"
                              : s.label === "Confirmed"
                                ? "#f59e0b"
                                : s.label === "Dispatched"
                                  ? "#8b5cf6"
                                  : s.label === "ReadyToDelivered"
                                    ? "#06b6d4"
                                    : "#ef4444",
                      })),
                      highlightScope: { faded: "global", highlighted: "item" },
                    },
                  ]}
                  height={300}
                  margin={{ right: 200 }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Only show Orders by Region for admin users, not shop/dispatch users */}
        {!isShopUser && !isDispatchUser && (
          <Grid item xs={12} lg={4}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <LocationOnIcon sx={{ color: "#3b82f6", mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Orders by Region
                  </Typography>
                </Box>
                {formData?.orders_by_region?.length > 0 ? (
                  <BarChart
                    xAxis={[
                      {
                        data: formData.orders_by_region.map(
                          (r) => r.region_name,
                        ),
                        scaleType: "band",
                      },
                    ]}
                    series={[
                      {
                        data: formData.orders_by_region.map(
                          (r) => r.order_count,
                        ),
                        label: "Orders",
                        color: "#3b82f6",
                      },
                    ]}
                    height={250}
                    margin={{ left: 50, right: 20, top: 40, bottom: 60 }}
                    borderRadius={16}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // Sales Tab Content
  const SalesTab = () => {
    const totalRevenue =
      formData?.sales_trends?.data?.reduce((sum, val) => sum + val, 0) || 0;
    const avgMonthly =
      totalRevenue / (formData?.sales_trends?.data?.length || 1);
    const maxSales = Math.max(...(formData?.sales_trends?.data || [0]));
    const peakMonthIndex = formData?.sales_trends?.data?.indexOf(maxSales);
    const peakMonth = formData?.sales_trends?.labels?.[peakMonthIndex];

    return (
      <Box>
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Monthly Sales Performance
            </Typography>
            {formData?.sales_trends?.labels?.length > 0 ? (
              <BarChart
                xAxis={[
                  {
                    data: formData.sales_trends.labels.map((label, idx) => idx),
                    scaleType: "band",
                    valueFormatter: (value) =>
                      formData.sales_trends.labels[value],
                  },
                ]}
                series={[
                  {
                    data: formData.sales_trends.data,
                    label: "Sales Revenue",
                    color: "#3b82f6",
                  },
                ]}
                height={400}
                margin={{ left: 80, right: 20, top: 40, bottom: 60 }}
                borderRadius={8}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No data
              </Typography>
            )}
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  gutterBottom
                >
                  Total Revenue
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#3b82f6">
                  ₹{totalRevenue.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {formatLabel(formData?.sales_trends?.labels?.[0])} -{" "}
                  {formatLabel(
                    formData?.sales_trends?.labels?.[
                      formData?.sales_trends?.labels?.length - 1
                    ],
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  gutterBottom
                >
                  Average Monthly
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#10b981">
                  ₹{Math.round(avgMonthly).toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Per month average
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  gutterBottom
                >
                  Peak Month
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#8b5cf6">
                  {peakMonth || "-"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  ₹{maxSales.toLocaleString()} revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Products Tab Content
  const ProductsTab = () => (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Top Selling Products
              </Typography>
              {formData?.top_selling_products?.length > 0 ? (
                <BarChart
                  yAxis={[
                    {
                      data: formData.top_selling_products.map(
                        (p) => p.product_name,
                      ),
                      scaleType: "band",
                    },
                  ]}
                  series={[
                    {
                      data: formData.top_selling_products.map(
                        (p) => p.total_quantity,
                      ),
                      label: "Quantity",
                      color: "#10b981",
                    },
                  ]}
                  layout="horizontal"
                  height={300}
                  margin={{ left: 150, right: 20, top: 40, bottom: 40 }}
                  borderRadius={8}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Top Categories
              </Typography>
              {formData?.top_selling_categories?.length > 0 ? (
                <BarChart
                  yAxis={[
                    {
                      data: formData.top_selling_categories.map(
                        (c) => c.category_name,
                      ),
                      scaleType: "band",
                    },
                  ]}
                  series={[
                    {
                      data: formData.top_selling_categories.map(
                        (c) => c.total_quantity,
                      ),
                      label: "Quantity",
                      color: "#f59e0b",
                    },
                  ]}
                  layout="horizontal"
                  height={300}
                  margin={{ left: 130, right: 20, top: 40, bottom: 40 }}
                  borderRadius={8}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Product Performance Summary
          </Typography>
          <TableContainer>
            <Table sx={{ "& td, & th": { textAlign: "center" } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Quantity Sold</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Market Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData?.top_selling_products?.map((product, idx) => {
                  const totalQty = formData.top_selling_products.reduce(
                    (sum, p) => sum + p.total_quantity,
                    0,
                  );
                  const marketShare = (
                    (product.total_quantity / totalQty) *
                    100
                  ).toFixed(1);
                  return (
                    <TableRow
                      key={idx}
                      hover
                      sx={{ "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {product.product_name}
                      </TableCell>
                      <TableCell align="right">
                        {product.total_quantity}
                      </TableCell>
                      <TableCell align="right">{marketShare}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  // Customers Tab Content
  const CustomersTab = () => {
    const totalCustomers = formData?.customer_growth?.new_customers || 0;
    const avgMonthly =
      totalCustomers / (formData?.customer_growth?.data?.length || 1);

    return (
      <Box>
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Customer Growth Trend
            </Typography>
            {formData?.customer_growth?.labels?.length > 0 ? (
              <LineChart
                xAxis={[
                  {
                    data: formData.customer_growth.labels.map(
                      (label, idx) => idx,
                    ),
                    scaleType: "point",
                    valueFormatter: (value) =>
                      formData.customer_growth.labels[value],
                  },
                ]}
                series={[
                  {
                    data: formData.customer_growth.data,
                    label: "New Customers",
                    color: "#8b5cf6",
                    curve: "linear",
                  },
                ]}
                height={350}
                margin={{ left: 60, right: 20, top: 40, bottom: 60 }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No data
              </Typography>
            )}
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  gutterBottom
                >
                  Total New Customers
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#8b5cf6">
                  {totalCustomers.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {`${formatLabel(formData?.customer_growth?.labels?.[0])} - ${formatLabel(formData?.customer_growth?.labels?.[formData?.customer_growth?.labels?.length - 1])}`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  gutterBottom
                >
                  Monthly Average
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#3b82f6">
                  {Math.round(avgMonthly)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  New customers per month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  gutterBottom
                >
                  Conversion Rate
                </Typography>
                <Typography variant="h4" fontWeight={700} color="#10b981">
                  {((formData?.conversion_rate || 0) * 100).toFixed(0)}%
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Excellent performance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return loader ? (
    <Loader />
  ) : (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      {/* Shop Info Banner - Only for shop users */}
      {isShopUser && (
        <Card
          elevation={2}
          sx={(theme) => ({
            mb: 3,
            bgcolor: theme.palette.mode === "dark" ? "#0a1929" : "#f0f9ff",
            borderLeft: "4px solid #3b82f6",
          })}
        >
          <CardContent>
            <Box display="flex" alignItems="center">
              <LocationOnIcon sx={{ color: "#3b82f6", mr: 1, fontSize: 28 }} />
              <Box>
                <Typography variant="h6" fontWeight={600} color="#3b82f6">
                  Shop Dashboard - {shopName}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ✅ DISPATCH INFO BANNER - Only for dispatch users */}
      {isDispatchUser && (
        <Card
          elevation={2}
          sx={(theme) => ({
            mb: 3,
            bgcolor: theme.palette.mode === "dark" ? "#2d1f00" : "#fef3c7",
            borderLeft: "4px solid #f59e0b",
          })}
        >
          <CardContent>
            <Box display="flex" alignItems="center">
              <LocalShippingIcon
                sx={{ color: "#f59e0b", mr: 1, fontSize: 28 }}
              />
              <Box>
                <Typography variant="h6" fontWeight={600} color="#f59e0b">
                  Dispatch Dashboard - {dispatchName}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  Region ID: {regionId}
                </Typography> */}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 3,
          py: 2,
          boxShadow: 2,
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ color: "text.primary" }}>
          {isShopUser
            ? "Shop Analytics Dashboard"
            : isDispatchUser
              ? "Dispatch Analytics Dashboard"
              : "Business Analytics Dashboard"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.5 }}>
          Real-time insights and performance metrics
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Box
        sx={{
          bgcolor: "background.paper",
          boxShadow: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ mx: "auto", px: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "capitalize",
                  fontSize: "1rem",
                  fontWeight: 500,
                  minHeight: 55,
                },
              }}
            >
              <Tab label="Overview" />
              <Tab label="Sales" />
              <Tab label="Products" />
              <Tab label="Customers" />
            </Tabs>

            <ToggleButtonGroup
              color="primary"
              value={type}
              exclusive
              onChange={(e, val) => val && setType(val)}
              size="small"
              sx={{ height: "fit-content" }}
            >
              <ToggleButton value="daily">Daily</ToggleButton>
              <ToggleButton value="weekly">Weekly</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mx: "auto", p: 3 }}>
        {selectedTab === 0 && <OverviewTab />}
        {selectedTab === 1 && <SalesTab />}
        {selectedTab === 2 && <ProductsTab />}
        {selectedTab === 3 && <CustomersTab />}
      </Box>
    </Box>
  );
}

export default DashboardIndex;
